/**
 * Favorited TIG shorts: in-memory list, written through to the custom API.
 *
 * The list is seeded from the customer metafield rendered into
 * window.tigShortFavoriteConfig.favorites, so reading costs no request. Writes
 * go to the app proxy:
 *
 *   POST   {appProxy}/tig/api/favorite-shorts        { shortId }
 *   DELETE {appProxy}/tig/api/favorite-shorts/{id}
 *
 * Both answer { success, favoritedShorts[] }, and report a business failure as
 * 200 with `success: false`, so the status alone does not say whether the write
 * landed. Two shapes of failure, told apart by `limitReached`:
 *
 *   already favorited, or already absent on delete: the desired state holds, so
 *   the toggle stands and this counts as success.
 *
 *   limitReached, with the cap in `limit`: the write was refused. The toggle has
 *   to be reverted, and `favoritedShorts` carries the list the server kept.
 *
 * The service appends on write, so its list is oldest first. Reversed here,
 * because the UI shows most recently saved first.
 *
 * add and remove resolve { ok, limitReached }, so callers can revert an
 * optimistic UI change, and tell a full list apart from a write that failed for
 * any other reason. A 401 sends the customer to the login page.
 *
 * Only the id is stored. Titles get edited in TIG and thumbnail filenames are
 * content hashes, so caching either goes stale; resolve them with fetchList.
 */
(function () {
  var config = window.tigShortFavoriteConfig || {};
  var appProxy = (config.appProxy || '').replace(/\/$/, '');
  var ENDPOINT = appProxy + '/tig/api/favorite-shorts';
  // Tig Short Contents List API v1.0. Staging is capi.stg.tigmedia.jp, but the
  // theme's TIG embeds point at production in every environment.
  var CAPI_LIST = 'https://capi.tigmedia.jp/v2/cl';

  // Mirrors the cap the service enforces, so a doomed write costs no request.
  // The service is the authority: it answers limitReached when this is wrong,
  // and that reply is what reverts the toggle.
  var MAX = config.max || 50;

  /** Server order is oldest first; the UI wants newest first. */
  function fromServer(list) {
    return (list || [])
      .map(String)
      .filter(function (id, index, all) {
        return id && all.indexOf(id) === index;
      })
      .reverse();
  }

  var ids = fromServer(config.favorites);

  function result(ok, limitReached) {
    return { ok: ok, limitReached: limitReached === true };
  }

  /**
   * Resolves { ok, limitReached }, and syncs ids from the returned list.
   * contentId is only used to reopen the short after a 401 sends the customer
   * through the login page.
   */
  function request(url, options, contentId) {
    return fetch(url, options)
      .then(function (response) {
        if (response.status === 401) {
          var login = window.TigShortFavoriteLoginRedirect;
          if (login) login.toLogin(contentId);
          else window.location.href = '/account/login';
          return result(false);
        }
        if (!response.ok) {
          console.warn('[tig-short-favorite-store]', options.method, url, response.status);
          return result(false);
        }
        return response
          .json()
          .then(function (body) {
            // The server's list wins whenever it sends one, including on the
            // refusals, where it is the state the write did not change.
            if (body && Array.isArray(body.favoritedShorts)) {
              ids = fromServer(body.favoritedShorts);
            }
            if (body && body.limitReached) {
              console.warn(
                '[tig-short-favorite-store] service limit of ' + (body.limit || MAX) + ' reached'
              );
              return result(false, true);
            }
            return result(true);
          })
          .catch(function () {
            // 2xx with an unreadable body: the write landed, the list did not.
            return result(true);
          });
      })
      .catch(function (error) {
        console.warn('[tig-short-favorite-store] request failed', url, error);
        return result(false);
      });
  }

  window.TigShortFavoriteStore = {
    MAX: MAX,

    /** Newest first. */
    ids: function () {
      return ids.slice();
    },

    has: function (id) {
      return ids.indexOf(String(id)) !== -1;
    },

    /** limitReached is set by the local guard as well as the service refusal. */
    add: function (id) {
      id = String(id);
      if (ids.indexOf(id) !== -1) return Promise.resolve(result(true));
      if (ids.length >= MAX) {
        console.warn('[tig-short-favorite-store] limit of ' + MAX + ' reached');
        return Promise.resolve(result(false, true));
      }
      return request(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortId: id }),
      }, id).then(function (written) {
        // Fallback for a 2xx that returned no usable list.
        if (written.ok && ids.indexOf(id) === -1) ids.unshift(id);
        // Every add goes through here, including the one after the limit dialog
        // frees a slot, so this is the single place the favourite is tracked.
        if (written.ok && window.logBrazeFavorite) {
          window.logBrazeFavorite('favorited_short', { short_id: id });
        }
        return written;
      });
    },

    remove: function (id) {
      id = String(id);
      return request(ENDPOINT + '/' + encodeURIComponent(id), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }, id).then(function (written) {
        if (written.ok) {
          ids = ids.filter(function (existing) {
            return existing !== id;
          });
          // Every removal goes through here, including the one the limit dialog
          // makes to free a slot.
          if (window.logBrazeFavorite) {
            window.logBrazeFavorite('unfavorited_short', { short_id: id });
          }
        }
        return written;
      });
    },

    /**
     * Animated hover preview. Not part of the documented API, but it is what
     * TIG's own tiles use. ~215KB, and it plays as soon as it is in the
     * document, so only attach it on hover.
     */
    previewUrl: function (id) {
      return 'https://contents.tigmedia.jp/' + encodeURIComponent(id) + '/preview.webp';
    },

    /** Still poster, preferring the smaller documented webp (15KB vs 53KB). */
    posterUrl: function (content) {
      return (content && (content.first_frame_image_url || content.thumbnail_url)) || '';
    },

    /**
     * Content for many ids in one request, per the Tig Short Contents List API
     * (GET /v2/cl, v1.0). Unauthenticated and CORS-enabled.
     *
     * Resolves { contents, unavailable }. Unpublished and deleted shorts are
     * omitted from the response rather than erroring, so anything requested and
     * not returned is reported as unavailable. A 404 arrives as HTTP 200 with a
     * populated error object, so the body has to be checked, not the status.
     */
    fetchList: function (requested) {
      var ids = (requested || []).map(String);
      if (ids.length === 0) return Promise.resolve({ contents: [], unavailable: [] });

      var params = new URLSearchParams();
      ids.forEach(function (id) {
        params.append('cid', id);
      });

      return fetch(CAPI_LIST + '?' + params.toString())
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var contents =
            data && !data.error && Array.isArray(data.contents) ? data.contents.slice() : [];

          // The API answers newest-created first, which is not the order the
          // customer saved them in. Restore the order we asked for.
          contents.sort(function (a, b) {
            return ids.indexOf(a.identifier) - ids.indexOf(b.identifier);
          });

          var available = contents.map(function (content) {
            return content.identifier;
          });
          return {
            contents: contents,
            unavailable: ids.filter(function (id) {
              return available.indexOf(id) === -1;
            }),
          };
        })
        .catch(function (error) {
          console.warn('[tig-short-favorite-store] content list failed', error);
          return { contents: [], unavailable: [] };
        });
    },
  };
})();
